import React from "react";

import { first } from "lodash/fp";

import type { UserDistributionSetting } from "@app/common/types";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import { DropdownMenuItem } from "@rui/Dropdown";
import type { DropdownItem } from "@rui/Dropdown/types";
import { getSelectedFromOptions } from "@rui/Dropdown/utils";
import TooltipWrapper from "@rui/TooltipWrapper";

import { SelectButton, SelectMenu } from "./styles";

const FrequencySelect = ({
  name,
  defaultValue,
  onChange,
  options,
  setting,
}: {
  name: string;
  defaultValue?: UserDistributionSetting["frequency"];
  onChange?: (updatedSetting: UserDistributionSetting) => void;
  options: DropdownItem<UserDistributionSetting["frequency"]>[];
  setting?: UserDistributionSetting;
}) => {
  const { anchorElement, onClose, onClick, open } = useAnchorElement();

  if (!setting) return null;

  if (options.length < 2 || !onChange) {
    return (
      <TooltipWrapper title="There are no other available options to choose from at this time.">
        <SelectButton label="Frequency" disabled name={name}>
          {first(options)?.label}
        </SelectButton>
      </TooltipWrapper>
    );
  }

  const selectedValue = setting?.frequency;

  const handleSelect = (value: UserDistributionSetting["frequency"]) => () => {
    onChange({
      ...setting,
      frequency: value,
    });
  };

  const selectedItem = getSelectedFromOptions(options, selectedValue);

  return (
    <>
      <SelectButton name={name} label="Frequency" onClick={onClick}>
        {selectedItem?.value}
      </SelectButton>
      <SelectMenu anchorEl={anchorElement} onClose={onClose} open={open}>
        {options.map((option) => (
          <DropdownMenuItem
            key={option.value}
            value={option.value}
            label={option.label}
            isDefault={option.value === defaultValue}
            onClick={handleSelect(option.value)}
          />
        ))}
      </SelectMenu>
    </>
  );
};

export default FrequencySelect;
